import React, { useMemo } from 'react';
import { CollapsibleDetailsSection } from '../../components';
import { PROFILE_DETAILS } from '@shared/data';
import { formatDate } from '@shared/utils/formatters';
import type { Customer } from '@shared/types/customers';
import type { PaymentDetail } from '../../types/panel-common.types';

interface ProfileSectionProps {
  isExpanded: boolean;
  onToggle: () => void;
  isDetailView?: boolean;
  onEdit?: () => void;
  customer?: Customer;
}

export const ProfileSection = React.memo<ProfileSectionProps>(({
  isExpanded,
  onToggle,
  isDetailView = false,
  onEdit,
  customer
}) => {
  const profileDetails: PaymentDetail[] = useMemo(() => {
    if (!customer) return PROFILE_DETAILS;

    const details: PaymentDetail[] = [
      { label: 'Customer ID', value: customer.id },
      { label: 'Customer since', value: formatDate(customer.created) },
      { label: 'Description', value: customer.description || '\u2014' },
      { label: 'Billing emails', value: customer.billingEmails?.join(', ') || customer.email },
      { label: 'Business name', value: customer.businessName || '\u2014' },
      { label: 'Individual name', value: customer.type === 'individual' ? customer.name : '\u2014' },
      { label: 'Billing details', value: customer.billingCountry || '\u2014' },
      { label: 'Language', value: customer.language || '\u2014' },
      { label: 'Tax location status', value: customer.taxLocationStatus || 'Not registered' },
      { label: 'Tax status and IDs', value: customer.taxStatus || 'Taxable' },
    ];

    return details;
  }, [customer]);

  return (
    <CollapsibleDetailsSection
      title="Customer"
      details={profileDetails}
      isExpanded={isExpanded}
      onToggle={onToggle}
      isDetailView={isDetailView}
      emptyMessage="No customer details available"
      actions={onEdit ? [
        {
          label: "Edit",
          onClick: onEdit
        }
      ] : undefined}
    />
  );
});

ProfileSection.displayName = 'ProfileSection';
